import type { AiPauseEvent, Sms } from '@/types/types'

/**
 * Whether a conversation should show the "AI is paused" affordances.
 *
 * Two conditions, and both matter. `is_ai_sms_paused` is the per-lead switch a
 * rep trips by replying by hand. `has_ai_sms_agent` is the gate that keeps the
 * marker off conversations that have no bot to pause — most numbers have no
 * SMS agent, and without it every thread in an account that has never touched
 * AI would advertise the feature.
 *
 * Shared because the thread and the conversation list read the same payload
 * and must agree: a rule enforced in one component and not the other is how a
 * paused lead ends up marked in the list but not in the thread it opens.
 */
export interface AiPauseConversation {
  lead?: { is_ai_sms_paused?: boolean } | null
  number?: { has_ai_sms_agent?: boolean } | null
}

export function isAiSmsPaused(conversation: AiPauseConversation | null | undefined): boolean {
  return !!conversation?.number?.has_ai_sms_agent && !!conversation?.lead?.is_ai_sms_paused
}

/** An AI pause event dressed to sit in the message stream. */
export type AiEventEntry = AiPauseEvent & { ai_event: true; sent_at: string }

export type TranscriptEntry = Sms | AiEventEntry

export function isAiEventEntry(entry: TranscriptEntry): entry is AiEventEntry {
  return (entry as AiEventEntry).ai_event === true
}

/**
 * The pause/resume events that belong in the loaded stretch of transcript.
 *
 * Gated on `has_ai_sms_agent` alone rather than the full pause predicate: the
 * dividers are the permanent record of what happened, so a hand-back has to
 * stay legible after the lead is no longer paused.
 *
 * Floored at the oldest loaded message because messages page in newest-first —
 * without the floor every pause in the lead's history would stack up above the
 * first bubble. Loading older messages lowers the floor and the earlier events
 * appear in place.
 */
export function visibleAiEvents(
  conversation: AiPauseConversation | null | undefined,
  events: AiPauseEvent[],
  messages: Sms[],
): AiEventEntry[] {
  if (!conversation?.number?.has_ai_sms_agent) return []
  if (!events.length || !messages.length) return []

  const floor = new Date(messages[0].sent_at).getTime()

  return (
    events
      .filter((event) => new Date(event.created_at).getTime() >= floor)
      .map((event) => ({ ...event, ai_event: true as const, sent_at: event.created_at }))
      // mergeAiEvents walks these with a single forward cursor, so they have to
      // ascend. The endpoint already sorts by id, but normalising here means the
      // transcript doesn't silently reorder if that ever changes. The sort is
      // stable, which matters: a pause and the resume that undoes it can share a
      // created_at to the second, and ties must keep the server's id order.
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  )
}

/**
 * Walk the ordered messages and drop each event in front of the first message
 * it precedes.
 *
 * Deliberately not a re-sort of the combined list: `sent_at` is briefly null on
 * a just-created outbound row, and a timestamp sort would shuffle the
 * transcript under the rep. A message with an unparseable time simply gets no
 * event inserted before it and keeps its place.
 */
export function mergeAiEvents(messages: Sms[], events: AiEventEntry[]): TranscriptEntry[] {
  if (!events.length) return messages

  // Event times are parsed once up front rather than per message: the common
  // case is a single pause at the end of the transcript, whose timestamp would
  // otherwise be re-parsed on every bubble it fails to precede.
  const eventTimes = events.map((event) => new Date(event.sent_at).getTime())

  const entries: TranscriptEntry[] = []
  let next = 0

  for (const message of messages) {
    const messageTime = new Date(message.sent_at).getTime()

    while (next < events.length && eventTimes[next] <= messageTime) {
      entries.push(events[next])
      next++
    }

    entries.push(message)
  }

  while (next < events.length) {
    entries.push(events[next])
    next++
  }

  return entries
}
